<template>
    <Row type="none" class="bg-white rounded shadow container">

        <div class="pt-3">
            <GoBackButton/>
        </div>

        <b-modal
            centered
            no-close-on-backdrop
            :hide-header="true"
            :hide-footer="true"
            id="sync"
        >

            <Paragraph class="pt-2 mb-0" style="font-size: 22px; font-weight: 600">{{ $t('inventory.syncing') }}</Paragraph>
            <b-progress :value="value" variant="primary"></b-progress>
        </b-modal>

        <div v-if="getInventoryDetails.inventoryItems.length" class="text-xl text-main font-bold pt-3">
            {{ $t('inventory.result') }}
        </div>

        <div v-if="getInventoryDetails.inventoryItems.length" class="grid grid-cols-12 gap-3 mt-4">
            <div class="border border-gray-700 rounded h-11 col-span-12 md:col-span-6 position-relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('inventory.allProducts') }}
                </div>
                <div class="pt-2 pl-3 font-bold text-gray-500">
                    {{ getInventoryDetails.inventoryItems.length }}
                </div>
            </div>

            <div class="border border-gray-700 rounded h-11 col-span-12 md:col-span-6 position-relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('inventory.nonFitProducts') }}
                </div>
                <div class="pt-2 pl-3 font-bold text-gray-500">
                    {{ getInventoryDetails.deficit + getInventoryDetails.profit }}
                </div>
            </div>

            <div class="border border-gray-700 rounded h-11 col-span-12 md:col-span-4 position-relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('inventory.fitProducts') }}
                </div>
                <div class="pt-2 pl-3 font-bold text-gray-500">
                    {{ getInventoryDetails.inventoryItems.length - (getInventoryDetails.deficit + getInventoryDetails.profit) }}
                </div>
            </div>

            <div class="border border-gray-700 rounded h-11 col-span-12 md:col-span-4 position-relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('inventory.profitProducts') }}
                </div>
                <div class="pt-2 pl-3 font-bold text-gray-500">
                    {{ getInventoryDetails.profit }}
                </div>
            </div>

            <div class="border border-gray-700 rounded h-11 col-span-12 md:col-span-4 position-relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('inventory.deficitProducts') }}
                </div>
                <div class="pt-2 pl-3 font-bold text-gray-500">
                    {{ getInventoryDetails.deficit }}
                </div>
            </div>
        </div>

        <div v-if="getInventoryDetails.inventoryItems.length" class="text-xl text-main font-bold pt-3">
            {{ $t('inventory.finance') }}
        </div>

        <div v-if="getInventoryDetails.inventoryItems.length" class="grid grid-cols-12 gap-3 mt-4">
            <div class="border border-gray-700 rounded h-11 col-span-12 position-relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 absolute -top-3">
                    {{ $t('inventory.sumSelectedProducts') }}
                </div>
                <div class="pt-2 pl-3 flex justify-center">
                    <div class="text-gray-500 font-bold px-5">
                        {{ showCurrencyById(baseCurrencyId, calcTotalSum) }}
                    </div>
                    <div>
                        <img src="/images/right-arrow%20-blue.png" alt="" class="h-4">
                    </div>
                    <div class="text-main font-bold px-5">
                        {{ showCurrencyById(baseCurrencyId, calcRealTotalSum) }}
                    </div>
                    <div>
                        <img src="/images/right-arrow%20-blue.png" alt="" class="h-4">
                    </div>
                    <div class="text-red-500 font-bold px-5">
                        {{ showCurrencyById(baseCurrencyId, getInventoryDetails.difference) }}
                    </div>
                </div>
            </div>

            <div class="border border-gray-700 rounded h-11 col-span-12 md:col-span-6 position-relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('inventory.sumFitProducts') }}
                </div>
                <div class="pt-2 pl-3 font-bold text-gray-500">
                    {{ showCurrencyById(baseCurrencyId, calcTotalSum - getInventoryDetails.difference) }}
                </div>
            </div>

            <div class="border border-gray-700 rounded h-11 col-span-12 md:col-span-6 position-relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('inventory.sumDifferenceProducts') }}
                </div>
                <div class="pt-2 pl-3 text-main font-bold">
                    {{ showCurrencyById(baseCurrencyId, getInventoryDetails.difference) }}
                </div>
            </div>

            <div class="border border-gray-700 rounded h-11 col-span-12 md:col-span-6 position-relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('inventory.sumProfitProducts') }}
                </div>
                <div class="pt-2 pl-3 text-green-500 font-bold">
                    {{ showCurrencyById(baseCurrencyId, calcProfitProductSum) }}
                </div>
            </div>

            <div class="border border-gray-700 rounded h-11 col-span-12 md:col-span-6 position-relative">
                <div class="font-bold text-sm bg-white px-1 text-fontColor z-30 position-absolute -top-3 ml-2">
                    {{ $t('inventory.sumDeficitProducts') }}
                </div>
                <div class="pt-2 pl-3 text-red-500 font-bold">
                    {{ showCurrencyById(baseCurrencyId, calcDeficitProductSum) }}
                </div>
            </div>
        </div>

        <InventoryMakeListTable
            :piw-list="getInventoryDetails.inventoryItems"
            :has-show-delete-button="false"
            :is-synced="getInventoryDetails.isSynced"
            class="mt-3"
        />

        <div v-if="getInventoryDetails.inventoryItems.length" class="row mt-4 pb-4">
            <div v-if="getInventoryDetails.isSynced" class="col-3">
                <SubmitRow
                    text="inventory.synced"
                    :is-loading="isLoading"
                    sm-size="12"
                    md-size="12"
                    :is-disabled="true"
                />
            </div>
            <div v-else class="col-3">
                <SubmitRow
                    text="inventory.sync"
                    :is-loading="isLoading"
                    :do-action="sync"
                    sm-size="12"
                    md-size="12"
                />

            </div>
        </div>

    </Row>
</template>

<script>
import GoBackButton from "@/components/GoBackButton.vue";
import {mapActions, mapGetters} from "vuex";
import InventoryMakeListTable from "@/components/InventoryMakeListTable.vue";
import currencyFormatMixin from "@/mixin/currencyFormatMixin";
import SubmitRow from "@/components/SubmitRow.vue";
import Paragraph from "@/components/html/Paragraph.vue";
import Row from "@/components/html/Row.vue";

export default {
    name: "InventoryListItem",
    components: {Row, Paragraph, SubmitRow, InventoryMakeListTable, GoBackButton},
    mixins: [currencyFormatMixin],
    data() {
        return {
            isLoading: false,
            totalSum: 0,
            realTotalSum: 0,
            value: 0
        }
    },
    computed: {
        ...mapGetters(['getInventoryDetails']),
        calcTotalSum() {
            let totalSum = 0

            this.getInventoryDetails.inventoryItems.forEach((item) => {
                totalSum += parseFloat(item.amount) * parseFloat(item.product.sellingPrice)
            })

            return totalSum
        },
        calcRealTotalSum() {
            let realTotalSum = 0

            this.getInventoryDetails.inventoryItems.forEach((item) => {
                realTotalSum += parseFloat(item.realAmount) * parseFloat(item.product.sellingPrice)
            })

            return realTotalSum
        },
        calcProfitProductSum() {
            let profitProductSum = 0

            this.getInventoryDetails.inventoryItems.forEach((item) => {
                if (parseFloat(item.realAmount) > parseFloat(item.amount)) {
                    profitProductSum += (parseFloat(item.realAmount) - parseFloat(item.amount)) * parseFloat(item.product.sellingPrice)
                }
            })

            return profitProductSum
        },
        calcDeficitProductSum() {
            let profitDeficitSum = 0

            this.getInventoryDetails.inventoryItems.forEach((item) => {
                if (parseFloat(item.realAmount) < parseFloat(item.amount)) {
                    profitDeficitSum += (parseFloat(item.amount) - parseFloat(item.realAmount)) * parseFloat(item.product.sellingPrice)
                }
            })

            return profitDeficitSum
        }
    },
    methods: {
        ...mapActions(['fetchInventoryDetails', 'makeSyncInventory', 'fetchInventoryDetails']),
        sync() {
            this.$bvModal.show('sync')
            setInterval(() => {
                this.value = this.value + 2
            }, 100)


            let data = {inventoryId: this.getInventoryDetails.id}
            this
                .makeSyncInventory(data)
                .then(() => {
                    this.fetchInventoryDetails(this.getInventoryDetails.id)
                })
        },
    },
    mounted() {
        if (this.$route.params.id) {
            this.fetchInventoryDetails(this.$route.params.id)
        }
    },
    watch: {
        'value'() {
            if (this.value === 100) {
                this.$bvModal.hide('sync')
            }
        }
    }
}
</script>

<style scoped>
.subtitle {
    color: #7E7E7E;
}

.digit-not-synced {
    color: #444444;
    font-weight: 600;
}

.digit-synced {
    color: #c9cdd0;
    font-weight: 600;
}

.synced {
    color: #c9cdd0;
    font-size: 12px;
    font-weight: 600;
    position: relative;
    top: -10px
}

.not-synced {
    color: red;
    font-size: 12px;
    font-weight: 600;
    position: relative;
    top: -10px
}

.sync {
    color: #c9cdd0;
}
</style>
